import { PureComponent, ReactNode } from 'react';
import PadV2AutoSuggest, {
    AllOptions,
    AS_OPTIONS_SEARCH,
} from '../../../rightPanel/workspace/padV2/PadV2AutoSuggest';
import { useDispatch, useSelector } from 'react-redux';
import { DentalProcedureIcon } from '../../../../common/Icons';
import { DentalProcedure } from '../../../../../store/types/prescription';
import fetchMatchingProcedures, {
    ProcedureSuggestions,
} from '../../../../../api/fetchMatchingProcedures';
import { getDentalProcedures } from '../../../../../store/selectors/prescriptionPad';
import { upsertDentalProcedure } from '../../../../../store/actions/prescriptionPad';
import { generateLocalIdForEntity, randomString } from '../../../../../store/utils';
import { getMe } from '../../../../../store/idbHandler';
import { LOCAL_ID_PREFIX } from '../../../../../constants';
import { AS_OPTIONS_FREQUENCTLY } from '../../../trackSource';
import { DENTAL_PROCEDURE_AS_EL_ID, DENTAL_PROCEDURE_AS_INPUT } from '../DentalProcedure';
import moment from 'moment';
import { timeObject } from '../../../rightPanel/workspace/padV2/LabTestsDateSelector';
import PadGenieAutoSuggestFuseManager, {
    DENTAL_PROCEDURES,
} from '../../../PadGenieAutoSuggestFuseManager';

type Props = {
    onSelect: (option: DentalProcedure) => void;
    elements: ReadonlyArray<DentalProcedure>;
    service_id?: string;
};

class DentalProcedureAutoSuggest extends PureComponent<Props> {
    getOptions = (
        setOptions: (options: AllOptions<DentalProcedure>, inputValue?: string) => void,
        input?: string,
    ): void => {
        const options: AllOptions<DentalProcedure> = [];
        const fuse = PadGenieAutoSuggestFuseManager.getFuseForType(DENTAL_PROCEDURES);
        const gElements = PadGenieAutoSuggestFuseManager.getOriginalListForType(DENTAL_PROCEDURES);

        if (!fuse) return;

        if (!input?.trim()) {
            const genieOptions = gElements?.filter(Boolean) || [];
            genieOptions.splice(7);
            options.push({
                label: AS_OPTIONS_FREQUENCTLY,
                options: genieOptions,
            });
            setOptions(options, input);
        } else {
            const filteredGItems = fuse
                .search(input)
                .map((option) => option.item)
                .filter(Boolean)
                .map((t) => ({ ...t, track: { source: 'GENIE' } }));

            filteredGItems.splice(7);

            const currentOptions = [
                { label: AS_OPTIONS_SEARCH as typeof AS_OPTIONS_SEARCH, options: filteredGItems },
            ];

            setOptions(currentOptions, input);

            fetchMatchingProcedures(input, 'dental')
                .then(async (response) => {
                    this.handleApiOptions(currentOptions, input, setOptions, response);
                })
                .catch(() => {
                    this.handleApiOptions(currentOptions, input, setOptions);
                });
        }
    };

    handleApiOptions = (
        currentOptions: AllOptions<DentalProcedure>,
        input: string,
        setOptions: (options: AllOptions<DentalProcedure>, inputValue?: string) => void,
        response?: ProcedureSuggestions[],
    ): void => {
        const allOptions = [...currentOptions[0].options];
        response?.filter(Boolean).forEach((i) => {
            if (!allOptions.find((s) => s.id === i.id)) {
                allOptions.push(i);
            }
        });
        allOptions.splice(7);
        const isMatchFound = allOptions.find(
            (i) => i.name.toLowerCase() === input.trim().toLowerCase(),
        );

        if (!isMatchFound && input) {
            const id = `${LOCAL_ID_PREFIX}dp-${new Date().getTime()}-${randomString(4)}`;
            allOptions.push({
                id: id,
                name: input,
                entity_id: generateLocalIdForEntity(id),
            });
        }
        setOptions([{ label: AS_OPTIONS_SEARCH, options: allOptions }], input);
    };

    OptionIcon = DentalProcedureIcon('w-12 h-12 fill-darwin-accent-medicines-pink-800');

    render(): ReactNode {
        const { elements, onSelect, service_id } = this.props;
        const elementId = service_id
            ? `${DENTAL_PROCEDURE_AS_INPUT}-${service_id}`
            : DENTAL_PROCEDURE_AS_INPUT;
        return (
            <PadV2AutoSuggest
                inputId={elementId}
                elId={DENTAL_PROCEDURE_AS_EL_ID}
                onSelect={onSelect}
                optionIconText={this.OptionIcon}
                optionIconBgClass="circle-mx"
                menuListClassName="mx-dropdown"
                optionWrapperClass="mx-dropdown-item"
                getOptions={this.getOptions}
                placeholder="Start typing a procedure..."
                elements={elements}
            />
        );
    }
}

export function toPxDentalProcedureFromApiObj(
    option: ProcedureSuggestions | DentalProcedure,
    service_id?: string,
): DentalProcedure {
    const dentalProcedure: DentalProcedure = {
        id: option.id,
        name: option.name,
        entity_id: generateLocalIdForEntity(option.id),
    };
    const actorName = getMe()?.user.fn || '';
    if (service_id) {
        dentalProcedure.service_id = service_id;
        dentalProcedure.start_on = moment().set(timeObject).toISOString();
        dentalProcedure.conducted_by = actorName;
    }
    return dentalProcedure;
}

export const DentalProcedureOnPadAutoSuggest: React.FC<{
    service_id?: string;
}> = ({ service_id }) => {
    const dispatch = useDispatch();
    const elements = useSelector(getDentalProcedures);

    const onSelect = (option: ProcedureSuggestions) => {
        const dentalProcedure = toPxDentalProcedureFromApiObj(option, service_id);
        dispatch(upsertDentalProcedure(dentalProcedure, elements.length));
        const elementId = service_id
            ? `${DENTAL_PROCEDURE_AS_INPUT}-${service_id}`
            : DENTAL_PROCEDURE_AS_INPUT;
        setTimeout(() => {
            const t = document.getElementById(elementId) as HTMLInputElement;
            t && t.focus();
        }, 500);
    };

    return (
        <DentalProcedureAutoSuggest
            onSelect={onSelect}
            elements={elements}
            service_id={service_id}
        />
    );
};
